import isEmpty from 'lodash/isEmpty'
import { Role, ICurrentUser, IFloor, IBuilding } from '@api'
import { IEmployeeLike, IPermissionsOnEmployee, IPermissionsOnEmployeeList } from './types'

function canLocationAdminManage(user: ICurrentUser, employee: IEmployeeLike) {
  const { spaceAssignment, secondarySpaceAssignments, neighborhoods } = employee

  // handle unassigned employees
  if (
    !secondarySpaceAssignments.length &&
    !neighborhoods.length &&
    (!spaceAssignment || !spaceAssignment.space)
  ) {
    return true
  }

  // handle employee seated on a primary seat
  const primarySeat =
    spaceAssignment &&
    spaceAssignment.space &&
    belongsToManagedLocations(spaceAssignment.space.floor.id, user.employee.locationAdmins)

  // handle employee seated on a secondary seat
  const secondarySeat = secondarySpaceAssignments.some(({ space }) =>
    belongsToManagedLocations(space.floor.id, user.employee.locationAdmins)
  )

  // handle employee seated in a neighborhood
  const neighborhood = neighborhoods.some(({ floor }) =>
    belongsToManagedLocations(floor.id, user.employee.locationAdmins)
  )

  return primarySeat || secondarySeat || neighborhood
}

export function canDeleteEmployees(user: ICurrentUser, employees: IEmployeeLike[]) {
  if (employees.find((e) => e.id === user.employee.id)) return false // cannot delete oneself
  return !employees.map((e) => onEmployee(user, e)).find((p) => !p.canDelete)
}

export function onEmployee(user: ICurrentUser, employee: IEmployeeLike): IPermissionsOnEmployee {
  const permissions = {
    canChangeRole: true,
    canEditNote: true,
    canToggleRemote: true,
    canEdit: true,
    canDelete: true,
  }
  if (user.isSuperUser) return permissions

  if (user.employee.role === Role.ADMIN || user.employee.role === Role.POWER_ADMIN) {
    return {
      ...permissions,
      canEdit: !employee.imported,
      canChangeRole: user.employee.role === Role.ADMIN,
      canDelete: !employee.imported,
    }
  }

  if (user.employee.role === Role.LOCATION_ADMIN) {
    const canManage = canLocationAdminManage(user, employee)

    const isSeated =
      !!employee.spaceAssignment ||
      !!employee.secondarySpaceAssignments ||
      !isEmpty(employee.neighborhoods)
    return {
      ...permissions,
      canChangeRole: false,
      canEditNote: isSeated && canManage,
      canToggleRemote: isSeated && canManage,
      canDelete: !employee.imported && canManage,
      canEdit: !employee.imported && canManage,
    }
  }

  if (user.employee.role === Role.SPACE_CAPTAIN) {
    const canManage = canLocationAdminManage(user, employee)
    return {
      ...permissions,
      canChangeRole: false,
      canEditNote: canManage,
      canToggleRemote: canManage,
      canDelete: !employee.imported && canManage,
      canEdit: !employee.imported && canManage,
    }
  }

  return {
    canChangeRole: false,
    canEditNote: false,
    canToggleRemote: false,
    canEdit: false,
    canDelete: false,
  }
}

export function onEmployeeList(
  user: ICurrentUser,
  list: IEmployeeLike[]
): IPermissionsOnEmployeeList {
  const permissions = {
    canChangeRole: true,
    canToggleRemote: true,
    canDelete: true,
    canImport: true,
  }

  if (user.isSuperUser) {
    return {
      ...permissions,
      // Cannot delete if list contains current user
      canDelete: list.every(({ id }) => id !== user.employee.id),
    }
  }

  if (user.employee.role === Role.ADMIN) {
    return {
      ...permissions,
      // Cannot delete if list contains current user or imported employees
      canDelete: list.every(({ id, imported }) => id !== user.employee.id && !imported),
    }
  }

  if (user.employee.role === Role.POWER_ADMIN) {
    return {
      ...permissions,
      canChangeRole: false,
      canImport: true,
      // Cannot delete if list contains current user or imported employees
      canDelete: list.every(({ id, imported }) => id !== user.employee.id && !imported),
    }
  }

  if (user.employee.role === Role.LOCATION_ADMIN) {
    const canManage = list.every((empl) => {
      return user.employee.id !== empl.id && canLocationAdminManage(user, empl)
    })

    return {
      // location admin cannot change role
      canChangeRole: false,
      // location admin cannot import or view imports
      canImport: false,
      canToggleRemote: canManage,
      canDelete: canManage,
    }
  }

  if (user.employee.role === Role.SPACE_CAPTAIN) {
    const canManage = list.every((empl) => {
      return user.employee.id !== empl.id && canLocationAdminManage(user, empl)
    })

    return {
      // SIQ Captain cannot change role
      canChangeRole: false,
      // SIQ Captain cannot import or view imports
      canImport: false,
      canToggleRemote: canManage,
      canDelete: canManage,
    }
  }

  if (user.employee.role === Role.BOOKINGS_MANAGER) {
    return {
      canChangeRole: false,
      canToggleRemote: false,
      canDelete: false,
      canImport: true,
    }
  }

  return {
    canChangeRole: false,
    canToggleRemote: false,
    canDelete: false,
    canImport: false,
  }
}

function belongsToManagedLocations(
  floorId: string,
  managedLocations: { floorPlan: IFloor & { building: IBuilding } }[]
) {
  return !!managedLocations.find(({ floorPlan: { id } }) => id === floorId)
}
